import React, { useContext, useEffect, useState } from "react";
import { Button, DatePicker, Form, Select, Space, Tag, TimePicker, Tooltip, } from "antd";
import CustomTable from "../../components/CustomTable";
import useApi from "../../hooks/useApi";
import useFilter from "../../hooks/useFilter";
import { AppStateContext } from "../../context/AppContext";
import { ShowToast, Severty } from "../../helper/toast";
import AcceptedImg from "../../assets/images/accepted-new-image.png";
import RejectedImg from "../../assets/images/rejected-new-image.png";
import RescheduledImg from "../../assets/images/rescheduled-new-image.png";
import CompletedImg from "../../assets/images/compleated-new-image.png";
import { Modal } from "antd";
import { ListClass, ListBooking, UpdateBooking, } from "../../constants/API/API";
import moment from "moment";
import callApi from "../../constants/API/CallApi";

const BOOKING_STATUS = [
  { value: "pending", label: "Pending" },
  { value: "cancelled", label: "Cancelled" },
  { value: "completed", label: "Completed" },
  { value: "rescheduled", label: "Rescheduled" },
  { value: "accepted", label: "Accepted" },
  { value: "missed", label: "Missed" },
];

const Booking = () => {
  const { setPageHeading } = useContext(AppStateContext);
  setPageHeading("Booking Management");

  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [rescheduleRecord, setRescheduleRecord] = useState(null);
  const [classList, setClassList] = useState([]);
  const [filters, setFilters] = useFilter({
    sortField: "createdOn",
    sort: "desc",
    studentId: undefined,
    classId: undefined,
    branchId: undefined,
    status: undefined,
    transcationType: undefined,
  });

  const handleStatusChange = (newStatus, record) => {
    setLoading(true);
    const payload = {
      status: newStatus,
      date: moment(selectedDate).format("YYYY-MM-DD HH:mm:ss.SSSSSS"),
      slot: selectedTime,
    };
    callApi({
      method: "PUT",
      callback: () => UpdateBooking(payload, record._id),
      onSuccess: () => {
        setLoading(false);
        setRefresh((x) => !x);
        ShowToast(`Booking marked as ${newStatus}`, Severty.SUCCESS);
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const handleStatusChangeWithConfirm = (status, record) => {
    Modal.confirm({
      title: `${status} Booking!`,
      content: `Are you sure you want to mark this booking's status as "${status}"?`,
      okText: "Yes",
      cancelText: "No",
      okButtonProps: { type: "primary", danger: status === "  " },
      onOk: () => handleStatusChange(status, record),
    });
  };

  const handleRescheduleClick = (record) => {
    setRescheduleRecord(record);
    setIsRescheduleModalOpen(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [classRes] = await Promise.all([ListClass()]);

        setClassList(classRes?.data?.list || []);
      } catch (error) {
        setClassList([]);
      }
    };
    fetchData();
  }, []);

  useApi({
    callback: ListBooking,
    payload: filters,
    dependencies: [refresh, filters],
    shouldCallApi: true,
    setLoading,
    onSuccess: (data) => {
      setData(data?.docs || []);
      setTotalDocs(data?.totalDocs);
      setLoading(false);
    },
    onError: () => {
      setLoading(false);
    },
  });

  const handleFilterChange = (key) => (value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1,
    }));
  };

  const handleExport = () => {
    if (!data || data.length === 0) {
      ShowToast("No data available to export", Severty.WARNING);
      return;
    }

    const headers = [
      "Booking ID",
      "Student Name",
      "Class",
      "Branch",
      "Date",
      "Slot",
      "Status",
      "Booked At",
    ];

    const csvData = data.map((record) => [
      record.bookingId || "-",
      record.user?.name || "-",
      record.class?.name || "-",
      record.branch?.name || "-",
      moment(record.date).format("ll") || "-",
      record.slot || "-",
      record.status || "-",
      record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "-",
    ]);

    // Combine headers and data
    const csvContent = [
      headers.join(","), // Header row
      ...csvData.map((row) => row.map((cell) => `"${cell}"`).join(",")), // Data rows with quotes to handle commas
    ].join("\n");

    // Create a Blob and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "transactions_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    ShowToast("Report exported successfully", Severty.SUCCESS);
  };

  const renderFilters = () => (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "5px" }}>
      <Space>
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Class"
          onChange={handleFilterChange("classId")}
          value={filters.classId}
          options={classList.map((cls) => ({
            label: cls.name,
            value: cls._id,
          }))}
        />
        <Select
          allowClear
          style={{ width: 200 }}
          placeholder="Filter by Status"
          onChange={handleFilterChange("status")}
          value={filters.status}
          options={BOOKING_STATUS}
        />
        <Button type="primary" onClick={handleExport}>
          Export Report
        </Button>
      </Space>
    </div>
  );

  const columns = [
    {
      title: "Booking ID",
      dataIndex: "bookingId",
      key: "bookingId",
      render: (_, { bookingId }) => bookingId || "-",
    },
    {
      title: "Student",
      dataIndex: "user",
      key: "user",
      render: (text, record) => record?.student?.name || record?.user?.name,
    },
    {
      title: "Parent",
      dataIndex: "user",
      key: "user",
      render: (text, record) => !record?.student?.name ? "Self" : record?.user?.name,
    },
    {
      title: "Class",
      dataIndex: "class",
      key: "class",
      render: (text, record) => record?.class?.name || "-",
    },
    {
      title: "Branch",
      dataIndex: "branch",
      key: "branch",
      render: (text, record) => record?.branch?.name || "-",
    },
    {
      title: "Trainer",
      dataIndex: "trainer",
      key: "trainer",
      render: (text, record) => record?.trainer?.name || "-",
    },
    {
      title: "Booking Date",
      dataIndex: "date",
      key: "date",
      render: (text, record) =>
        record?.date ? moment(record?.date).format("ll") : "-",
    },
    {
      title: "Time Slot",
      dataIndex: "slot",
      key: "slot",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (_, { status }) => {
        let color =
          status === "cancelled"
            ? "red"
            : status === "accepted"
              ? "green"
              : status === "completed"
                ? "darkgreen"
                : status === "rescheduled"
                  ? "orange"
                  : "blue";
        const formattedStatus =
          status.charAt(0).toUpperCase() + status.slice(1);

        return <Tag color={color}>{formattedStatus}</Tag>;
      },
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => (text ? moment(text).format("ll") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <div className="main-newsmall-class-buttons">
          <Tooltip color="purple" title="Mark as Accepted">
            <Button
              type="primary"
              onClick={() => handleStatusChangeWithConfirm("accepted", record)}
            >
              <img src={AcceptedImg} alt="Accepted" />
            </Button>
          </Tooltip>

          <Tooltip color="purple" title="Mark as Cancelled">
            <Button
              type="primary"
              onClick={() => handleStatusChangeWithConfirm("cancelled", record)}
            >
              <img src={RejectedImg} alt="Cancelled" />
            </Button>
          </Tooltip>

          <Tooltip color="purple" title="Reschedule Booking">
            <Button
              type="primary"
              onClick={() => handleRescheduleClick(record)}
            >
              <img src={RescheduledImg} alt="Rescheduled" />
            </Button>
          </Tooltip>

          <Tooltip color="purple" title="Mark as Completed">
            <Button
              type="primary"
              onClick={() => handleStatusChangeWithConfirm("completed", record)}
            >
              <img src={CompletedImg} alt="Completed" />
            </Button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        data={data}
        loading={loading}
        filters={filters}
        setFilters={setFilters}
        columns={columns}
        totalDocs={totalDocs}
        searchField={["name"]}
        searchPlaceholder={"Search by Student Name"}
        customFilter={renderFilters}
        hideAddButton
      />

      <Modal
        title="Reschedule Booking"
        open={isRescheduleModalOpen}
        className="tab_modal"
        onCancel={() => {
          setSelectedDate();
          setSelectedTime();
          setIsRescheduleModalOpen(false);
        }}
        onOk={() => {
          console.log("Rescheduling to:", selectedDate, selectedTime);
          // setIsRescheduleModalOpen(false);
          handleStatusChange("rescheduled", rescheduleRecord);
        }}
        okText="Reschedule"
      >
        <Form layout="vertical">
          <Form.Item label="Select New Date">
            <DatePicker
              value={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              style={{ width: "100%" }}
            // format="YYYY-MM-DD HH:mm:ss.SSSSSS"
            />
          </Form.Item>
          <Form.Item label="Select New Time">
            <TimePicker.RangePicker
              // value={selectedTime}
              placeholder={["StartTime", "EndTime"]}
              onChange={(time) => {
                let startTime = time[0]?.format("HH:mm");
                let endTime = time[1]?.format("HH:mm");
                let slot = `${startTime}-${endTime}`;
                setSelectedTime(slot);
              }}
              style={{ width: "100%" }}
              format="HH:mm"
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Booking;
